import React, { useState } from 'react'
import { IoChevronDown, IoChevronUp, IoTrendingUp, IoTrendingDown, IoRemove } from 'react-icons/io5'
import type {
  ProjectionsHub,
  ExplainabilitySummary,
  ExplainabilityDetailed,
  ScenarioHub,
  OddsComparisonHub,
} from '../../../types'

interface ProjectionsPanelProps {
  projections: ProjectionsHub
  explainability: ExplainabilitySummary
  explainabilityDetailed?: ExplainabilityDetailed | null
  scenarios: ScenarioHub[]
  odds?: OddsComparisonHub | null
}

export function ProjectionsPanel({
  projections,
  explainability,
  explainabilityDetailed,
  scenarios,
  odds,
}: ProjectionsPanelProps) {
  const [showDetailedBreakdown, setShowDetailedBreakdown] = useState(false)
  const [activeScenario, setActiveScenario] = useState<string | null>(null)

  // Get direction icon
  const DirectionIcon = ({ direction }: { direction: 'up' | 'down' | 'neutral' }) => {
    switch (direction) {
      case 'up':
        return <IoTrendingUp size={14} className="direction-icon up" />
      case 'down':
        return <IoTrendingDown size={14} className="direction-icon down" />
      default:
        return <IoRemove size={14} className="direction-icon neutral" />
    }
  }

  // Format factor value
  const formatFactorValue = (value: number, direction: 'up' | 'down' | 'neutral') => {
    if (direction === 'neutral') return value.toFixed(1)
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}`
  }

  // Get active projection (with scenario if selected)
  const getActiveProjection = () => {
    if (activeScenario) {
      const scenario = scenarios.find((s) => s.name === activeScenario)
      if (scenario) {
        return scenario.adjusted_total
      }
    }
    return projections.total_yellow_cards
  }

  return (
    <div className="cards-projections-panel">
      {/* Main Projections */}
      <div className="projections-header">
        <h3 className="projections-title">Projections</h3>
      </div>

      {/* Line Projections */}
      <div className="projections-grid">
        <div className="projection-card projection-card-main">
          <span className="projection-value">{getActiveProjection().toFixed(1)}</span>
          <span className="projection-label">Total Cards</span>
          {activeScenario && (
            <span className="projection-scenario-label">({activeScenario})</span>
          )}
        </div>
        <div className="projection-card">
          <span className="projection-value">{projections.home_yellow_cards.toFixed(1)}</span>
          <span className="projection-label">Home</span>
        </div>
        <div className="projection-card">
          <span className="projection-value">{projections.away_yellow_cards.toFixed(1)}</span>
          <span className="projection-label">Away</span>
        </div>
      </div>

      {/* Line Probabilities */}
      {(projections.over_2_5_prob || projections.over_3_5_prob || projections.over_4_5_prob) && (
        <div className="projections-lines">
          <h4 className="projections-section-title">Line Probabilities</h4>
          <div className="projections-lines-grid">
            {projections.over_2_5_prob != null && (
              <div className="projection-line">
                <span className="projection-line-label">Over 2.5</span>
                <div className="projection-line-bar">
                  <div
                    className="projection-line-fill"
                    style={{ width: `${projections.over_2_5_prob * 100}%` }}
                  />
                </div>
                <span className="projection-line-value">{(projections.over_2_5_prob * 100).toFixed(0)}%</span>
              </div>
            )}
            {projections.over_3_5_prob != null && (
              <div className="projection-line">
                <span className="projection-line-label">Over 3.5</span>
                <div className="projection-line-bar">
                  <div
                    className="projection-line-fill"
                    style={{ width: `${projections.over_3_5_prob * 100}%` }}
                  />
                </div>
                <span className="projection-line-value">{(projections.over_3_5_prob * 100).toFixed(0)}%</span>
              </div>
            )}
            {projections.over_4_5_prob != null && (
              <div className="projection-line">
                <span className="projection-line-label">Over 4.5</span>
                <div className="projection-line-bar">
                  <div
                    className="projection-line-fill"
                    style={{ width: `${projections.over_4_5_prob * 100}%` }}
                  />
                </div>
                <span className="projection-line-value">{(projections.over_4_5_prob * 100).toFixed(0)}%</span>
              </div>
            )}
            {projections.over_5_5_prob != null && (
              <div className="projection-line">
                <span className="projection-line-label">Over 5.5</span>
                <div className="projection-line-bar">
                  <div
                    className="projection-line-fill"
                    style={{ width: `${projections.over_5_5_prob * 100}%` }}
                  />
                </div>
                <span className="projection-line-value">{(projections.over_5_5_prob * 100).toFixed(0)}%</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top Players */}
      {projections.top_players.length > 0 && (
        <div className="projections-top-players">
          <h4 className="projections-section-title">Top Card Risks</h4>
          <div className="projections-top-players-list">
            {projections.top_players.slice(0, 5).map((player, i) => (
              <div key={player.player_id} className="projection-player">
                <span className="projection-player-rank">{i + 1}</span>
                <span className="projection-player-name">{player.player_name}</span>
                <span className="projection-player-prob">{(player.probability * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Explainability Breakdown */}
      <div className="projections-explainability">
        <div className="projections-explainability-header">
          <h4 className="projections-section-title">Why This Projection</h4>
          {explainabilityDetailed && (
            <button
              type="button"
              className="projections-expand-btn"
              onClick={() => setShowDetailedBreakdown(!showDetailedBreakdown)}
            >
              {showDetailedBreakdown ? 'Show Less' : 'Show All'}
              {showDetailedBreakdown ? <IoChevronUp size={14} /> : <IoChevronDown size={14} />}
            </button>
          )}
        </div>

        {/* Summary View */}
        <div className="projections-factors">
          {explainability.factors.map((factor, i) => (
            <div key={i} className={`projection-factor ${factor.direction}`}>
              <DirectionIcon direction={factor.direction} />
              <span className="projection-factor-name">{factor.factor}</span>
              <span className="projection-factor-value">
                {formatFactorValue(factor.value, factor.direction)}
              </span>
            </div>
          ))}
          <div className="projection-factor baseline">
            <IoRemove size={14} className="direction-icon neutral" />
            <span className="projection-factor-name">League Baseline</span>
            <span className="projection-factor-value">{explainability.league_baseline.toFixed(1)}</span>
          </div>
        </div>

        {/* Detailed View */}
        {showDetailedBreakdown && explainabilityDetailed && (
          <div className="projections-detailed">
            {explainabilityDetailed.referee_factors.length > 0 && (
              <div className="projections-factor-group">
                <h5 className="projections-factor-group-title">Referee Factors</h5>
                {explainabilityDetailed.referee_factors.map((factor, i) => (
                  <div key={i} className={`projection-factor ${factor.direction}`}>
                    <DirectionIcon direction={factor.direction} />
                    <span className="projection-factor-name">{factor.factor}</span>
                    <span className="projection-factor-value">
                      {formatFactorValue(factor.value, factor.direction)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {explainabilityDetailed.team_factors.length > 0 && (
              <div className="projections-factor-group">
                <h5 className="projections-factor-group-title">Team Factors</h5>
                {explainabilityDetailed.team_factors.map((factor, i) => (
                  <div key={i} className={`projection-factor ${factor.direction}`}>
                    <DirectionIcon direction={factor.direction} />
                    <span className="projection-factor-name">{factor.factor}</span>
                    <span className="projection-factor-value">
                      {formatFactorValue(factor.value, factor.direction)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {explainabilityDetailed.context_factors.length > 0 && (
              <div className="projections-factor-group">
                <h5 className="projections-factor-group-title">Context Factors</h5>
                {explainabilityDetailed.context_factors.map((factor, i) => (
                  <div key={i} className={`projection-factor ${factor.direction}`}>
                    <DirectionIcon direction={factor.direction} />
                    <span className="projection-factor-name">{factor.factor}</span>
                    <span className="projection-factor-value">
                      {formatFactorValue(factor.value, factor.direction)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {explainabilityDetailed.h2h_factors.length > 0 && (
              <div className="projections-factor-group">
                <h5 className="projections-factor-group-title">H2H Factors</h5>
                {explainabilityDetailed.h2h_factors.map((factor, i) => (
                  <div key={i} className={`projection-factor ${factor.direction}`}>
                    <DirectionIcon direction={factor.direction} />
                    <span className="projection-factor-name">{factor.factor}</span>
                    <span className="projection-factor-value">
                      {formatFactorValue(factor.value, factor.direction)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Scenarios */}
      {scenarios.length > 0 && (
        <div className="projections-scenarios">
          <h4 className="projections-section-title">What If Scenarios</h4>
          <div className="projections-scenarios-list">
            {scenarios.map((scenario) => (
              <button
                key={scenario.name}
                type="button"
                className={`projection-scenario ${activeScenario === scenario.name ? 'active' : ''}`}
                onClick={() => setActiveScenario(activeScenario === scenario.name ? null : scenario.name)}
              >
                <span className="projection-scenario-name">{scenario.name}</span>
                <span className="projection-scenario-adjustment">
                  {scenario.adjustment > 0 ? '+' : ''}{scenario.adjustment.toFixed(1)}
                </span>
                <span className="projection-scenario-total">{scenario.adjusted_total.toFixed(1)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Odds Comparison */}
      {odds?.enabled && odds.total_line != null && (
        <div className="projections-odds">
          <h4 className="projections-section-title">vs Market Line</h4>
          <div className="projections-odds-comparison">
            <div className="projections-odds-row">
              <span className="projections-odds-label">Our Projection</span>
              <span className="projections-odds-value">{projections.total_yellow_cards.toFixed(1)}</span>
            </div>
            <div className="projections-odds-row">
              <span className="projections-odds-label">Market Line</span>
              <span className="projections-odds-value">O/U {odds.total_line}</span>
            </div>
            {odds.edge != null && (
              <div className={`projections-odds-edge ${odds.lean || ''}`}>
                <span className="projections-odds-edge-label">Edge</span>
                <span className="projections-odds-edge-value">
                  {odds.edge > 0 ? '+' : ''}{odds.edge.toFixed(1)} cards
                </span>
                {odds.lean && odds.lean !== 'neutral' && (
                  <span className={`projections-odds-lean ${odds.lean}`}>
                    Lean {odds.lean.toUpperCase()}
                  </span>
                )}
              </div>
            )}
            {odds.over_odds != null && odds.under_odds != null && (
              <div className="projections-odds-prices">
                <span className="projections-odds-price">Over @ {odds.over_odds.toFixed(2)}</span>
                <span className="projections-odds-price">Under @ {odds.under_odds.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
